import { checkboxesToUntick, parsePageValidation } from "@patient-watch/forms/pageValidation";
import { useEffect, useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";

/**
 * Keeps a page's exclusive checkboxes ("None of the above", "Prefer not to say") apart: ticking one
 * unticks the others of its list, and ticking any of those unticks it. Driven by the page's
 * `exclusive` rules in pages.page_validation; does nothing on pages without them.
 */
export function useExclusiveCheckboxes(form: UseFormReturn<Record<string, unknown>>, rawPageValidation: unknown) {
    const validation = useMemo(() => {
        const parsed = parsePageValidation(rawPageValidation);
        return parsed?.rules.some((rule) => rule.type === "exclusive") ? parsed : null;
    }, [rawPageValidation]);

    useEffect(() => {
        if (!validation) return;
        const subscription = form.watch((values, { name, type }) => {
            // Only the patient's own ticks; the unticks below arrive without a type and are ignored.
            if (!name || type !== "change") return;
            for (const id of checkboxesToUntick(validation, name, values)) {
                form.setValue(id, false, { shouldDirty: true });
            }
        });
        return () => subscription.unsubscribe();
    }, [form, validation]);
}
